import type { PlateElementProps } from 'platejs/react';
import { useEditorReadOnly, useEditorRef } from 'platejs/react';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

import {
  ImageElementContainer,
  type ImageSize,
  ImageStateWrapper,
  UploadProgressBar,
} from './image-element-parts';

export type { ImageSize } from './image-element-parts';

export interface ImageNodeData {
  type: 'img';
  fileId?: number;
  url: string;
  alt?: string;
  width?: ImageSize;
  // Upload state
  uploading?: boolean;
  uploadProgress?: number;
  uploadError?: string;
  uploadRetryCount?: number;
  // URL resolution state (for file:ID format)
  pendingUrlResolution?: boolean;
  urlResolutionError?: string;
}

interface ImageElementProps extends PlateElementProps {
  onRetryUpload?: (node: ImageNodeData) => void;
  onShowSizeMenu?: (element: HTMLElement, node: ImageNodeData) => void;
}

export const ImageElement: React.FC<ImageElementProps> = ({
  attributes,
  children,
  element,
  onRetryUpload,
  onShowSizeMenu,
}) => {
  const node = element as unknown as ImageNodeData;

  const readOnly = useEditorReadOnly();
  const editor = useEditorRef();
  const imageRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isSelected, setIsSelected] = useState(false);

  let widthStyle: string | number = 400;
  if (typeof node.width === 'number') {
    widthStyle = node.width;
  } else if (node.width === 'small') {
    widthStyle = 200;
  } else if (node.width === 'large') {
    widthStyle = '100%';
  }

  // Returns a stable mouse-down handler for a given resize corner
  const makeResizeHandler = useCallback(
    (corner: 'left' | 'right') => (e: React.MouseEvent) => {
      if (readOnly) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      // Capture values locally to avoid stale closure issues
      const startX = e.clientX;
      const startWidth = imageRef.current?.offsetWidth || 400;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta =
          corner === 'right'
            ? moveEvent.clientX - startX
            : startX - moveEvent.clientX;
        const newWidth = Math.max(100, startWidth + delta);
        const path = editor.api.findPath(element);
        if (path) {
          editor.tf.setNodes({ width: newWidth }, { at: path });
        }
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [readOnly, editor, element]
  );

  const handleMouseDownLeft = useMemo(
    () => makeResizeHandler('left'),
    [makeResizeHandler]
  );
  const handleMouseDownRight = useMemo(
    () => makeResizeHandler('right'),
    [makeResizeHandler]
  );

  const handleContextMenu = useCallback(
    (_e: React.MouseEvent) => {
      if (readOnly) {
        return;
      }
      _e.preventDefault();
      if (onShowSizeMenu && imageRef.current) {
        onShowSizeMenu(imageRef.current, node);
      }
    },
    [readOnly, node, onShowSizeMenu]
  );

  const handleClick = useCallback(
    (_e: React.MouseEvent) => {
      if (!readOnly) {
        setIsSelected(true);
        if (onShowSizeMenu && imageRef.current) {
          onShowSizeMenu(imageRef.current, node);
        }
      }
    },
    [readOnly, node, onShowSizeMenu]
  );

  const handleBlur = useCallback(() => {
    setIsSelected(false);
  }, []);

  const handleRetryClick = useCallback(() => {
    if ((node.uploadRetryCount ?? 0) >= 3) {
      console.warn(
        '[ImageElement] Max retry attempts reached for image upload'
      );
      return;
    }
    onRetryUpload?.(node);
  }, [onRetryUpload, node]);

  if (node.pendingUrlResolution) {
    return (
      <div {...attributes} contentEditable={false}>
        <ImageStateWrapper containerRef={imageRef} width={node.width}>
          <LoadingIndicator size='xs' />
          <span className='text-sm text-neutral-500'>Loading image…</span>
        </ImageStateWrapper>
        {children}
      </div>
    );
  }

  if (node.urlResolutionError) {
    return (
      <div {...attributes} contentEditable={false}>
        <ImageStateWrapper containerRef={imageRef} width={node.width}>
          <span className='text-sm text-neutral-600'>Image unavailable</span>
        </ImageStateWrapper>
        {children}
      </div>
    );
  }

  if (node.uploading) {
    return (
      <div {...attributes} contentEditable={false}>
        <ImageStateWrapper containerRef={imageRef} width={node.width}>
          <LoadingIndicator size='xs' />
          <span className='text-sm text-neutral-500'>
            Uploading…{' '}
            {node.uploadProgress ? `${Math.round(node.uploadProgress)}%` : ''}
          </span>
          {node.uploadProgress !== undefined && (
            <UploadProgressBar progress={node.uploadProgress} />
          )}
        </ImageStateWrapper>
        {children}
      </div>
    );
  }

  if (node.uploadError) {
    return (
      <div {...attributes} contentEditable={false}>
        <ImageStateWrapper
          containerRef={imageRef}
          width={node.width}
          borderClass='border-red-200 bg-red-50'
        >
          <svg
            className='w-8 h-8 text-red-500 mb-2'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
          <span className='text-sm text-red-600 mb-2'>Upload failed</span>
          <span className='text-xs text-red-500 mb-3'>{node.uploadError}</span>
          {onRetryUpload && (
            <button
              type='button'
              onClick={handleRetryClick}
              className='px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors'
            >
              Retry
            </button>
          )}
        </ImageStateWrapper>
        {children}
      </div>
    );
  }

  return (
    <div {...attributes} contentEditable={false} data-plate-image>
      <ImageElementContainer
        containerRef={imageRef}
        width={node.width}
        className={`relative inline-block rounded-lg overflow-hidden max-w-full transition-shadow ${
          isSelected && !readOnly ? 'ring-2 ring-blue-500 shadow-lg' : ''
        }`}
        onClick={handleClick}
        onBlur={handleBlur}
        onContextMenu={handleContextMenu}
        tabIndex={readOnly ? undefined : 0}
      >
        <img
          src={node.url}
          alt={node.alt || 'Inline image'}
          className='block w-full h-auto'
          draggable={false}
        />

        {isSelected && !readOnly && (
          <>
            <div
              className='absolute left-0 top-0 bottom-0 w-3 cursor-ew-resize flex items-center justify-center hover:bg-blue-500/20'
              onMouseDown={handleMouseDownLeft}
            >
              <div className='w-1 h-8 bg-blue-500 rounded-full opacity-70' />
            </div>

            <div
              className='absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize flex items-center justify-center hover:bg-blue-500/20'
              onMouseDown={handleMouseDownRight}
            >
              <div className='w-1 h-8 bg-blue-500 rounded-full opacity-70' />
            </div>

            <div
              className='absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize bg-blue-500 rounded-tl-lg opacity-70 hover:opacity-100'
              onMouseDown={handleMouseDownRight}
            />
            <div
              className='absolute bottom-0 left-0 w-4 h-4 cursor-nesw-resize bg-blue-500 rounded-tr-lg opacity-70 hover:opacity-100'
              onMouseDown={handleMouseDownLeft}
            />
          </>
        )}

        {isResizing && (
          <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/75 text-white px-2 py-1 rounded text-xs'>
            {typeof widthStyle === 'number' ? `${widthStyle}px` : widthStyle}
          </div>
        )}
      </ImageElementContainer>
      {children}
    </div>
  );
};

export default ImageElement;
